import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import BigHr from 'assets/templates/dividers/BigHr';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { groupBy, isEmpty, keys, sumBy } from 'lodash';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import Currency from 'models/enums/Currency';
import LineItem from 'models/enums/LineItem';
import LossCodeSelect from 'module/claims/components/lossCode/LossCodeSelect';
import ClaimDocumentForm from 'module/public-web/views/claimSubmit/components/Claim.DocumentForm';
import { Fragment, useEffect, useMemo } from 'react';
import DocumentPreview from '../../../../../assets/components/other/DocumentPreview';
import { addJobItemRowButtonStyles } from '../../claimEdit/sections/claimJobEdit/claimJobEditItem/ClaimEdit.Job.Item';
import ClaimJobDataPointsInputs, { getVisibleDataPoints } from '../other/Claim.Job.DataPoints.Inputs';
import useClaimSimpleClaim from './Claim.SimpleClaim.Hooks';
import { getLineItemLabel } from 'module/claims/helper/LineItem.Label';

function OneDocForm(props: {
  claimTypeDocument: ClaimTypeDocument;
  claimJobIndex?: number;
  form: Hooks.Form.Export<
    Api.Core.Claim.SaveSimple.IRequest & {
      uploadableClaimTypeDocuments: Model.IClaimTypeDocument[];
    }
  >;
}) {
  const { lang } = getLocales();
  const form = props.form;
  const claimJobs = form.data?.claimJobs || [];
  const claimJob = claimJobs[props.claimJobIndex];

  const isAlreadySubmitted = claimJob?.claimJobDocuments?.some(
    (cjd) => cjd.claimTypeDocumentId === props.claimTypeDocument.id
  );

  const docForm = useForm<{
    documents: Array<Utils.FormData<Omit<Model.IClaimJobDocument, 'claimTypeDocumentId'>>>;
  }>({
    default: {
      documents: [],
    },
    onSubmit: async ({ documents }) => {
      for (const document of documents) {
        const updatedClaimJobs = [...claimJobs];
        const currentJob = { ...updatedClaimJobs[props.claimJobIndex] };
        currentJob.claimJobDocuments = [
          ...(currentJob.claimJobDocuments ?? []),
          { claimTypeDocumentId: props.claimTypeDocument.id, ...document },
        ];
        updatedClaimJobs[props.claimJobIndex] = currentJob;
        form.merge({ claimJobs: updatedClaimJobs });
      }
      docForm.reload();
    },

    validation: (data, errors) => {
      if (data.documents?.some((it) => !it.name)) errors.documents = { name: lang.required };
      if (props.claimTypeDocument.required && !isAlreadySubmitted && data.documents?.some((it) => !it.formFile))
        errors.documents = { formFile: { _objectError: lang.required } };
    },
  });

  const uploadedFiles = useMemo(
    () =>
      docForm.data?.documents?.map((doc) => ({
        original: doc.formFile,
        type: doc.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(doc.formFile),
        name: doc.name,
        notes: doc.notes,
      })),
    [docForm.data.documents]
  );

  const hasUploadedDocs = claimJob?.claimJobDocuments?.some(
    (it) => it.claimTypeDocumentId === props.claimTypeDocument.id
  );
  const allowMoreUploads = !hasUploadedDocs ? true : !!props.claimTypeDocument.allowMultiple;
  const isDisabled = !allowMoreUploads || docForm.hasErrors;
  return (
    <Box style={{ marginBottom: Theme.Size.L }}>
      <InputWrapper style={{ marginBottom: Theme.Size.L }}>
        <FileInputSimple
          name="files"
          label={props.claimTypeDocument.name}
          value={uploadedFiles}
          onChange={({ files }) => {
            docForm.update({
              documents: [
                ...(docForm.data.documents ?? []),
                ...files.map((file) => ({
                  formFile: file.original,
                  name: file.original?.name,
                  approved: !props.claimTypeDocument.approvalRequired,
                })),
              ],
            });
          }}
          fileLimit={allowMoreUploads ? 5 : 1}
          error={docForm.errors?.documents?.formFile?._objectError}
          disabled={!allowMoreUploads}
        />
        <Button
          render={lang.add}
          onClick={docForm.submit}
          disabled={isDisabled}
          active={!docForm.hasErrors}
          class={ThemeButtonCircle}
          style={{ flexShrink: 0 }}
        />
      </InputWrapper>
      <DataMap
        data={uploadedFiles}
        render={({ data: uploadedFile, index }) => (
          <Fragment key={index}>
            <br />
            <br />
            <InputWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={uploadedFile.name}
                error={!uploadedFile.name ? lang.required : undefined}
                onChange={(data) => {
                  docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                  docForm.update({ documents: [...docForm.data.documents] });
                }}
              />
              {props.claimTypeDocument?.allowNotes && (
                <TextInput
                  name="notes"
                  label={lang.notes}
                  value={uploadedFile.notes}
                  onChange={(data) => {
                    docForm.data.documents[index] = { ...docForm.data.documents[index], ...data };
                    docForm.update({ documents: [...docForm.data.documents] });
                  }}
                />
              )}
            </InputWrapper>
          </Fragment>
        )}
      />
    </Box>
  );
}
export default function ClaimSimpleClaimForm(props: {
  contract: Model.IContract;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const { lang, translate } = getLocales();
  const { dealerId, isDealer, permissions } = useAuthStore();
  const {
    form,
    claimTypes,
    dealerListApi,
    uploadableDocuments,
    shouldShowClaimJobs,
    repairFacilityListApi,
    isEditingTax,
    setIsEditingTax,
    resetClaimData,
    updateClaimJob,
    addClaimJobItem,
    updateClaimJobItem,
    deleteClaimJobItem,
    updateClaimJobDataPointValue,
    updateClaimJobClaimType,
    addNewClaimJob,
    deleteClaimJob,
  } = useClaimSimpleClaim(props);

  useEffect(() => {
    if (isDealer) {
      form.update({ claimCreationOption: ClaimCreatorOptions.DEALERSHIP, dealerId: dealerId });
    }
  }, [isDealer]);

  return (
    <Modal
      subcomponents={{
        ...ThemeModalDefault,
        page: {
          ...ThemeModalDefault.page,
          style: {
            pageWrapper: {
              width: '75%',
            },
          },
        },
      }}
      header={lang.createSimpleClaim}
      onClose={props.onClose}
      render={
        <FormWrapper>
          <p
            style={{
              backgroundColor: Theme.Color.warning,
              color: 'white',
              padding: `${Theme.Size.M} ${Theme.Size.L}`,
              fontWeight: 'bold',
              marginTop: 0,
            }}
          >
            {lang.publicClaimCreationWarning}
          </p>
          <br />
          <SelectInput
            name="claimCreationOption"
            label={lang.whoIsSubmittingTheClaim}
            data={keys(ClaimCreatorOptions)
              .filter((it) => it !== ClaimCreatorOptions.CUSTOMER && it !== ClaimCreatorOptions.OTHER)
              .map((it) => ({
                id: it,
                title: translate(it).replace(/_/g, ' '),
              }))}
            value={form.data?.claimCreationOption}
            error={form.errors?.claimCreationOption}
            onChange={({ claimCreationOption }) => {
              form.update({
                ...resetClaimData(form.data),
                claimCreationOption,
                dealerId: undefined,
                dealer: undefined,
                repairFacility: undefined,
                repairFacilityId: undefined,
              });
            }}
            disabled={isDealer}
          />
          {ClaimCreatorOptions.REPAIR_FACILITY === form.data?.claimCreationOption && (
            <SelectInput
              name="repairFacilityId"
              label={lang.repairFacility}
              data={repairFacilityListApi.payload?.data?.map((it) => it.displayInfo)}
              value={form.data?.repairFacilityId}
              error={form.errors?.repairFacilityId}
              onChange={({ repairFacilityId }) => {
                form.update({ ...resetClaimData(form.data), repairFacilityId, dealerId: undefined, dealer: undefined });
              }}
              icon={<Icon class="fas-user" />}
            />
          )}
          {ClaimCreatorOptions.DEALERSHIP === form.data?.claimCreationOption && (
            <SelectInput
              name="dealerId"
              label={lang.dealer}
              data={dealerListApi?.payload?.data?.map((it) => it.displayInfo)}
              value={form.data.dealerId}
              onChange={({ dealerId }) => {
                form.update({
                  ...resetClaimData(form.data),
                  dealerId,
                  repairFacility: undefined,
                  repairFacilityId: undefined,
                });
              }}
              icon={<Icon class="fas-user" />}
              error={form.errors?.dealerId}
            />
          )}
          &nbsp;
          {shouldShowClaimJobs && (
            <>
              {(form.data?.claimJobs || []).map((claimJob, claimJobIndex) => {
                const groupedClaimJobItems = groupBy(claimJob?.claimJobItems, (it) => it.jobItemTypeCode);
                return (
                  <div key={claimJobIndex}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h2>
                        {lang.claimJob} {claimJobIndex + 1}
                      </h2>
                      <Button
                        class={ThemeButtonCircle}
                        media={<Icon class="fas-trash" />}
                        render={lang.delete}
                        data={claimJobIndex}
                        active
                        disabled={form.data.claimJobs?.length === 1}
                        onClick={deleteClaimJob}
                      />
                    </div>
                    <SelectInput
                      name="claimTypeId"
                      label={`${lang.claimType} #${claimJobIndex + 1}`}
                      data={claimTypes?.map((it) => it.displayInfo)}
                      value={claimJob.claimTypeId}
                      onChange={({ claimTypeId }) =>
                        updateClaimJobClaimType(
                          {
                            ...claimJob,
                            claimTypeId,
                          },
                          claimJobIndex
                        )
                      }
                      icon={<Icon class="fas-clipboard-list" />}
                      error={!claimJob.claimTypeId && lang.required}
                      disabled={isEmpty(dealerListApi.payload?.data)}
                      isLoading={dealerListApi.isExecuting}
                    />
                    &nbsp;
                    <MultilineTextInput
                      name="customerComplaint"
                      label={lang.customerComplaint}
                      value={claimJob.customerComplaint}
                      onChange={({ customerComplaint }) =>
                        updateClaimJob({ ...claimJob, customerComplaint }, claimJobIndex)
                      }
                    />
                    &nbsp;
                    <MultilineTextInput
                      name="cause"
                      label={lang.causeOfFailure}
                      value={claimJob.cause}
                      onChange={({ cause }) => updateClaimJob({ ...claimJob, cause }, claimJobIndex)}
                    />
                    &nbsp;
                    <MultilineTextInput
                      name="correction"
                      label={lang.correctionDetails}
                      value={claimJob.correction}
                      onChange={({ correction }) => updateClaimJob({ ...claimJob, correction }, claimJobIndex)}
                    />
                    &nbsp;
                    {!!getVisibleDataPoints(claimJob?.dataPoints).length && (
                      <div>
                        <h2>{lang.details}</h2>
                        <ClaimJobDataPointsInputs
                          dataPoints={getVisibleDataPoints(claimJob?.dataPoints)}
                          onUpdate={(value, id) => updateClaimJobDataPointValue(value, id, claimJobIndex)}
                        />
                      </div>
                    )}
                    {!!claimJob?.claimJobItems?.length &&
                      form.data?.claimCreationOption === ClaimCreatorOptions.DEALERSHIP && (
                        <div style={{ margin: Theme.Size.S }}>
                          <h2>{`${lang.jobItemTemplates} #${claimJobIndex + 1}`}</h2>
                          {[LineItem.part, LineItem.labour, LineItem.deductible, LineItem.other]
                            .filter((type) => groupedClaimJobItems[type]?.length)
                            ?.map((typeCode) => (
                              <div key={typeCode}>
                                <h4>{getLineItemLabel(typeCode)}</h4>
                                <table className={ThemeTableSimple} style={{ fontSize: Theme.FontSize.S }}>
                                  <thead>
                                    <tr>
                                      <th style={{ padding: '10px', width: '20px' }}>#</th>
                                      <th style={{ textAlign: 'center' }}>{lang.payee}</th>
                                      {typeCode !== LineItem.deductible && (
                                        <th style={{ textAlign: 'center' }}>{lang.lossCode}</th>
                                      )}
                                      {typeCode === LineItem.part && (
                                        <>
                                          <th style={{ textAlign: 'center' }}>{lang.baseCost}</th>
                                          <th style={{ textAlign: 'center' }}>{lang.quantity}</th>
                                        </>
                                      )}
                                      <th style={{ textAlign: 'center' }}>{lang.requestedAmount}</th>
                                      {typeCode !== LineItem.deductible && (
                                        <>
                                          <th style={{ textAlign: 'center' }}>{lang.gstHst}</th>
                                          <th style={{ textAlign: 'center' }}>{lang.pst}</th>
                                        </>
                                      )}
                                      <th style={{ textAlign: 'center' }}>{lang.total}</th>
                                      {claimJob?.claimJobItems?.some(
                                        (it) => !it.isCreatedFromTemplate && it.jobItemTypeCode !== LineItem.deductible
                                      ) && <th style={{ textAlign: 'center' }}></th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedClaimJobItems[typeCode]?.map((item, idx) => (
                                      <tr key={idx}>
                                        <td style={{ width: '20px', padding: '10px' }}>{idx + 1}</td>
                                        <td style={{ width: '15%' }}>{item.claimJobItemPayee?.dealer?.name}</td>
                                        {item.jobItemTypeCode !== LineItem.deductible && (
                                          <td style={{ width: '140px' }}>
                                            <LossCodeSelect
                                              width="140px"
                                              lossCodes={item.lossCodes}
                                              value={item?.lossCode}
                                              disabled={item.lossCodes?.length === 1}
                                              error={!item.lossCode && lang.required}
                                              onChange={({ lossCode }) =>
                                                updateClaimJobItem('lossCode', lossCode, item, claimJobIndex)
                                              }
                                            />
                                          </td>
                                        )}
                                        {item.jobItemTypeCode === LineItem.part && (
                                          <>
                                            <td>
                                              <NumberInput
                                                name="baseCost"
                                                value={Number(item?.baseCost?.toFixed(2))}
                                                onChange={({ baseCost }) =>
                                                  updateClaimJobItem('baseCost', baseCost, item, claimJobIndex)
                                                }
                                                step={0.01}
                                                style={{ input: { textAlign: 'right' } }}
                                                iconBefore={<Icon class="fas-dollar-sign" />}
                                              />
                                            </td>
                                            <td>
                                              <NumberInput
                                                name="quantity"
                                                value={Number(item?.quantity)}
                                                onChange={({ quantity }) =>
                                                  updateClaimJobItem('quantity', quantity, item, claimJobIndex)
                                                }
                                                step={1}
                                                style={{ input: { textAlign: 'center' } }}
                                              />
                                            </td>
                                          </>
                                        )}
                                        <td>
                                          <NumberInput
                                            name="correction"
                                            value={Number(item?.correction?.toFixed(2) ?? 0)}
                                            onChange={({ correction }) =>
                                              updateClaimJobItem('correction', correction, item, claimJobIndex)
                                            }
                                            step={0.01}
                                            style={{ input: { textAlign: 'right' } }}
                                            iconBefore={<Icon class="fas-dollar-sign" />}
                                            readOnly={
                                              item.jobItemTypeCode === LineItem.deductible ||
                                              item.jobItemTypeCode === LineItem.part
                                            }
                                          />
                                        </td>
                                        {item.jobItemTypeCode !== LineItem.deductible && (
                                          <>
                                            <td>
                                              <NumberInput
                                                name="gstHst"
                                                value={Number(((item?.gstHst || 0.0) * item?.adjudication)?.toFixed(2))}
                                                onChange={({ gstHst }) =>
                                                  updateClaimJobItem(
                                                    'gstHst',
                                                    gstHst / item.adjudication,
                                                    item,
                                                    claimJobIndex
                                                  )
                                                }
                                                onClick={() => {
                                                  setIsEditingTax(true);
                                                }}
                                                step={0.01}
                                                readOnly={!isEditingTax}
                                                htmlElementProps={{
                                                  title: !isEditingTax ? lang.clickToEdit : undefined,
                                                }}
                                                error={
                                                  item?.gstHst > 1 &&
                                                  lang.mustBeEqualOrLessThanAmount.replace(
                                                    '{amount}',
                                                    `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                                  )
                                                }
                                                style={{
                                                  input: { textAlign: 'right', cursor: 'text' },
                                                  cursor: 'text',
                                                }}
                                                iconBefore={<Icon class="fas-dollar-sign" />}
                                              />
                                            </td>
                                            <td>
                                              <NumberInput
                                                name="provincialSalesTax"
                                                value={Number(
                                                  ((item?.provincialSalesTax || 0.0) * item?.adjudication)?.toFixed(2)
                                                )}
                                                onChange={({ provincialSalesTax }) =>
                                                  updateClaimJobItem(
                                                    'provincialSalesTax',
                                                    provincialSalesTax / item?.adjudication,
                                                    item,
                                                    claimJobIndex
                                                  )
                                                }
                                                onClick={() => {
                                                  setIsEditingTax(true);
                                                }}
                                                step={0.01}
                                                readOnly={!isEditingTax}
                                                htmlElementProps={{
                                                  title: !isEditingTax ? lang.clickToEdit : undefined,
                                                }}
                                                error={
                                                  item?.provincialSalesTax > 1 &&
                                                  lang.mustBeEqualOrLessThanAmount.replace(
                                                    '{amount}',
                                                    `${lang.requestedAmount}: ${item?.adjudication?.toFixed(2)}`
                                                  )
                                                }
                                                style={{
                                                  input: { textAlign: 'right', cursor: 'text' },
                                                  cursor: 'text',
                                                }}
                                                iconBefore={<Icon class="fas-dollar-sign" />}
                                              />
                                            </td>
                                          </>
                                        )}
                                        <td style={{ width: '15%', padding: '0 12px 0 0' }}>
                                          <NumberInput
                                            name="total"
                                            value={Number(item?.total?.toFixed(2) ?? 0)}
                                            readOnly
                                            iconBefore={<Icon class="fas-dollar-sign" />}
                                            onChange={form.update}
                                          />
                                        </td>
                                        {!item.isCreatedFromTemplate &&
                                          item.jobItemTypeCode !== LineItem.deductible && (
                                            <td style={{ padding: Theme.Size.S }}>
                                              <Button
                                                class={ThemeButtonCircle}
                                                media={<Icon class="fas-trash" />}
                                                onClick={() => {
                                                  deleteClaimJobItem(claimJobIndex, typeCode, idx);
                                                }}
                                                htmlElementProps={{
                                                  title: lang.delete,
                                                }}
                                              />
                                            </td>
                                          )}
                                      </tr>
                                    ))}
                                  </tbody>
                                  {typeCode !== LineItem.deductible &&
                                    claimJob?.claimJobItems?.some((it) => !it.isCreatedFromTemplate) && (
                                      <tfoot>
                                        <tr>
                                          <td
                                            colSpan={typeCode === LineItem.part ? 10 : 8}
                                            style={{ padding: Theme.Size.S }}
                                          >
                                            <Button
                                              style={addJobItemRowButtonStyles}
                                              class={ThemeButtonCircle}
                                              media="fas-plus"
                                              onClick={() => addClaimJobItem(claimJobIndex, typeCode)}
                                              render={getLineItemLabel(typeCode)}
                                              htmlElementProps={{
                                                title: `${lang.addNew} ${getLineItemLabel(typeCode)} ${lang.item}`,
                                              }}
                                            />
                                          </td>
                                        </tr>
                                      </tfoot>
                                    )}
                                </table>
                              </div>
                            ))}
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <strong>
                                {lang.totalAmountWithoutTaxes}:{' '}
                                {formatNumberWithSeparator(
                                  sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.ttl).toFixed(2)
                                )}{' '}
                                {Currency.CAD}
                              </strong>
                              <strong>
                                {lang.totalAmountWithTaxes}:{' '}
                                {formatNumberWithSeparator(
                                  sumBy(ClaimJob.aggregates(claimJob as ClaimJob), (it) => it.total).toFixed(2)
                                )}{' '}
                                {Currency.CAD}
                              </strong>
                            </div>
                          </div>
                        </div>
                      )}
                    {!form.data?.claimJobs?.some((job) =>
                      uploadableDocuments.some((doc) => doc.claimTypeId === job.claimTypeId)
                    ) ? null : (
                      <>
                        <h2>{`${lang.claim} ${lang.documents} - ${lang.job} #${claimJobIndex + 1}`}</h2>
                        {!isEmpty(
                          form?.data?.uploadableClaimTypeDocuments?.filter(
                            (doc) =>
                              doc.required &&
                              !new Set(claimJob?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId))?.has(doc.id)
                          )
                        ) &&
                          uploadableDocuments.some(
                            (doc) =>
                              doc.claimTypeId === claimJob.claimTypeId &&
                              doc.required &&
                              !claimJob?.claimJobDocuments?.some((cjd) => cjd.claimTypeDocumentId === doc.id)
                          ) && (
                            <p
                              style={{
                                backgroundColor: Theme.Color.error,
                                color: 'white',
                                padding: `${Theme.Size.M} ${Theme.Size.L}`,
                                fontWeight: 'bold',
                                marginBottom: Theme.Size.XS,
                              }}
                            >
                              {lang.requiredFilesForTheFollowingDocs}:
                              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                                <div style={{ marginLeft: Theme.Size.M }}>
                                  {uploadableDocuments
                                    .filter(
                                      (doc) =>
                                        doc.claimTypeId === claimJob?.claimTypeId &&
                                        doc.required &&
                                        !claimJob?.claimJobDocuments?.some((cjd) => cjd.claimTypeDocumentId === doc.id)
                                    )
                                    .map((doc) => (
                                      <li key={doc.id}>{doc.name}</li>
                                    ))}
                                </div>
                              </ul>
                            </p>
                          )}
                        &nbsp;
                        <DataMap
                          data={uploadableDocuments.filter((it) => it.claimTypeId === claimJob?.claimTypeId)}
                          render={({ data: doc }) => (
                            <OneDocForm
                              key={doc.id}
                              claimTypeDocument={doc}
                              form={form}
                              claimJobIndex={claimJobIndex}
                            />
                          )}
                        />
                        <DocumentPreview
                          isDownloadDisabled
                          documents={claimJob?.claimJobDocuments?.map((it, docIdx) => ({
                            id: it.id,
                            name: it?.name,
                            mimeType: it.mediaFile?.contentType,
                            storedFileName: it.mediaFile?.name,
                            description: it.notes,
                            approved: it.approved,
                            documentName:
                              uploadableDocuments.find(
                                (ud) => ud.id === it.claimTypeDocumentId && ud.claimTypeId === claimJob?.claimTypeId
                              )?.name ?? lang.document,
                            documentType: lang.document,
                            onDelete: () => {
                              const updatedJobs = [...(form.data?.claimJobs ?? [])];
                              const updatedDocuments = claimJob.claimJobDocuments?.filter((_, i) => i !== docIdx);
                              updatedJobs[claimJobIndex].claimJobDocuments = updatedDocuments;
                              form.update({ claimJobs: updatedJobs });
                            },
                          }))}
                        />
                      </>
                    )}
                    <BigHr />
                  </div>
                );
              })}
              {!form.data?.claimJobs?.some((job) =>
                uploadableDocuments.some((doc) => doc.claimTypeId === job.claimTypeId)
              ) &&
                permissions?.UPLOAD_CLAIMDOCUMENT && (
                  <>
                    <h2>{lang.documents}</h2>
                    <ClaimDocumentForm
                      onSubmit={(data) => form.update({ claimDocuments: [...form.data.claimDocuments, ...data] })}
                    />
                    <DocumentPreview
                      documents={form.data.claimDocuments.map((it, docIdx) => ({
                        id: it.id,
                        url: it.mediaFile?.url,
                        name: it?.name,
                        extension: it.mediaFile?.extension,
                        description: it?.notes,
                        info: lang.new,
                        documentType: lang.document,
                        onDelete: () => {
                          const newDocs = form.data.claimDocuments.filter((_, idx) => idx !== docIdx);
                          form.update({ claimDocuments: newDocs });
                        },
                      }))}
                    />
                  </>
                )}
            </>
          )}
          <h2>{lang.info}</h2>
          <NumberInput
            name="odometerReading"
            label={lang.odometerReading}
            value={form.data?.odometerReading}
            onChange={form.update}
          />
          <TextInput
            name="workOrderNumber"
            label={lang.workOrderNumber}
            value={form.data?.workOrderNumber}
            onChange={form.update}
          />
        </FormWrapper>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: Theme.Size.S }}>
          <Button
            onClick={addNewClaimJob}
            media={<Icon class="fas-plus" />}
            render={`${lang.addNew} ${lang.claimJob}`}
            active
            disabled={
              !form.data?.contract ||
              !(form.data?.dealerId || form.data?.repairFacilityId) ||
              dealerListApi.isExecuting ||
              repairFacilityListApi.isExecuting
            }
            isLoading={form.isSubmitted}
            class={ThemeButtonCircle}
            htmlElementProps={{ title: `${lang.addNew} ${lang.claimJob}` }}
          />
          <Button
            onClick={form.submit}
            media={<Icon class="fas-save" />}
            render={lang.finish}
            active={!form.hasErrors && form.isUpdated}
            disabled={
              form.hasErrors || !form.isUpdated || dealerListApi.isExecuting || repairFacilityListApi.isExecuting
            }
            isLoading={form.isSubmitted}
            class={ThemeButtonCircle}
            htmlElementProps={{ title: lang.finish }}
          />
        </div>
      }
    />
  );
}
